import React from 'react'
import { IColumnTypes, sortDirections } from 'components/spreadsheet/types'
import {
  TEXT_COLUMN_TYPES,
  NUMBER_COLUMN_TYPES,
  DATE_COLUMN_TYPES,
  SELECT_COLUMN_TYPES
} from 'components/spreadsheet/constants/const'
import { Sort01, Sort10, SortAscending, SortAZ, SortDescending, SortNewest, SortOldest, SortZA } from 'components/icons'

export interface DirectionOption {
  value: sortDirections
  /** The direction's name: in the segment's tooltip, and as its row in the list. */
  label: string
  icon: React.ReactNode
}

/** A sort's (or a group's) two directions, drawn for what the column holds: A→Z, 0→1, oldest first. */
export const directionOptions = (kind: IColumnTypes | undefined): DirectionOption[] => {
  const [asc, desc] = directionsFor(kind)
  return [
    { value: sortDirections.asc, ...asc },
    { value: sortDirections.desc, ...desc }
  ]
}

type Direction = Omit<DirectionOption, 'value'>

const directionsFor = (kind: IColumnTypes | undefined): [Direction, Direction] => {
  if (kind && NUMBER_COLUMN_TYPES.includes(kind)) {
    return [
      { label: 'Smallest first', icon: <Sort01 /> },
      { label: 'Largest first', icon: <Sort10 /> }
    ]
  }
  if (kind && DATE_COLUMN_TYPES.includes(kind)) {
    return [
      { label: 'Oldest first', icon: <SortOldest /> },
      { label: 'Newest first', icon: <SortNewest /> }
    ]
  }
  if (
    kind &&
    (TEXT_COLUMN_TYPES.includes(kind) || SELECT_COLUMN_TYPES.includes(kind) || kind === 'link' || kind === 'attachment')
  ) {
    return [
      { label: 'A to Z', icon: <SortAZ /> },
      { label: 'Z to A', icon: <SortZA /> }
    ]
  }
  if (kind === 'checkbox') {
    return [
      { label: 'Unticked first', icon: <SortAscending /> },
      { label: 'Ticked first', icon: <SortDescending /> }
    ]
  }
  if (kind === 'vote') {
    return [
      { label: 'Fewest votes first', icon: <SortAscending /> },
      { label: 'Most votes first', icon: <SortDescending /> }
    ]
  }
  return [
    { label: 'Ascending', icon: <SortAscending /> },
    { label: 'Descending', icon: <SortDescending /> }
  ]
}

/** "Sort A to Z", "Sort oldest first": a direction's name as a sort action, for the column menus. */
export const sortLabel = (direction: string): string =>
  `Sort ${direction.replace(/^[A-Z](?=[a-z])/, (letter) => letter.toLowerCase())}`
